'use client';

import { FC, PropsWithChildren } from 'react';

import { UserGroups } from '@ravago/shared/authentication';
import { AccessLevel } from '@ravago/shared/page-data/enums/access-level';
import { ContentfulCaseStudy, ContentfulCollectionName } from '@ravago/shared/page-data/models/contentful';

import { Entry } from 'contentful';

import AuthGuardService from '../../../services/auth-guard/auth-guard.service';

// TODO: move this to server https://www.freecodecamp.org/news/secure-routes-in-next-js/
interface Props extends PropsWithChildren {
   isConsumer: boolean;
   accessLevels?: AccessLevel[];
   language?: string;
   contentEntity?: Entry<unknown>;
   contentType?: ContentfulCollectionName;
   redirectUserToLogin: boolean;
}

const userGroupsByAccessLevel: Record<AccessLevel, UserGroups[]> = {
   [AccessLevel.PUBLIC]: [UserGroups.public],
   [AccessLevel.GUEST]: [UserGroups.digitalPlatformGuest],
   [AccessLevel.PENDING]: [UserGroups.digitalPlatformPendingVerification],
   [AccessLevel.CUSTOMER]: [UserGroups.customerPortal],
   [AccessLevel.INTERNAL]: [
      UserGroups.ravagoGlobalGroupAdmin,
      UserGroups.ravagoSalesRepresentative,
      UserGroups.ravagoGlobalViewer,
      UserGroups.ravagoGlobalEditor
   ],
   // These will be deprecated in a different ticket.
   // They need to stay for now to support the Contentful page access level field.
   [AccessLevel.AUTHENTICATED]: [
      UserGroups.customerPortal,
      UserGroups.ravagoGlobalGroupAdmin,
      UserGroups.ravagoSalesRepresentative,
      UserGroups.ravagoGlobalViewer,
      UserGroups.digitalPlatformGuest,
      UserGroups.digitalPlatformPendingVerification,
      UserGroups.ravagoGlobalEditor
   ],
   [AccessLevel.VERIFIED]: [
      UserGroups.customerPortal,
      UserGroups.ravagoGlobalGroupAdmin,
      UserGroups.ravagoSalesRepresentative,
      UserGroups.ravagoGlobalViewer
   ],
   [AccessLevel.CSR]: [UserGroups.ravagoGlobalGroupAdmin, UserGroups.ravagoSalesRepresentative, UserGroups.ravagoGlobalViewer]
};

const AuthGuard: FC<Props> = ({ accessLevels, contentEntity, language, isConsumer, contentType, redirectUserToLogin, children }) => {
   if (!isConsumer) return children;

   let userGroups: UserGroups[] = [];

   accessLevels?.forEach((accessLevel) => {
      if (userGroupsByAccessLevel[accessLevel]) {
         userGroups.push(...userGroupsByAccessLevel[accessLevel]);
      }
   });

   if (contentType === 'caseStudy') {
      if ((contentEntity?.fields as ContentfulCaseStudy)?.visibility !== 'External') {
         userGroups = [UserGroups.ravagoGlobalGroupAdmin, UserGroups.ravagoSalesRepresentative, UserGroups.ravagoGlobalViewer];
      }
   }

   return AuthGuardService.isAuth(children, redirectUserToLogin, language, userGroups);
};

export default AuthGuard;
